/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { useCommonStyles } from "../common/Styles/commonStyles";
import { Button, Input, InputOnChangeData, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { EditRegular } from "@fluentui/react-icons";
import { appTokens } from "../common/Styles/appTokens";


const useLabelStyles = makeStyles({
    root: {
        minWidth: '350px',
        boxSizing: 'border-box',
    },
    hoverNotEditing: {
        border: `1px solid transparent !important`,
        borderRadius: '8px',
        '&:hover': {
            border: `1px solid ${appTokens.text.colors.subtle} !important`,
        },
    },
    whileEditing: {
        fontWeight: 'normal',
        minWidth: '350px',
    },
    whileNotEditing: {
        fontWeight: 600,
        border: '1px solid transparent !important',
        '::after': {
            border: '0px !important',
        },
    },
    emptyButRequired: {
        backgroundColor: tokens.colorBrandBackground2,
    },
});
type EditableLabelProps = {
    name?: string;
    readOnly?: boolean;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => void;
    placeholder?: string;
    labelClassName?: string;
    tabIndex?: number;
    required?: boolean;
}
export const EditableLabel = (props: EditableLabelProps) => {

    const { name, readOnly, value, onChange, placeholder, labelClassName, tabIndex, required = false } = props;

    //State
    const [editing, setEditing] = useState<boolean>(!value);
    const [userEdited, setUserEdited] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useLabelStyles();
    const inputClasses = mergeClasses(
        commonStyles.utility.withHiddenChildren,
        styles.root,
        !editing && labelClassName,
        !editing && !readOnly && styles.hoverNotEditing,
        !editing && styles.whileNotEditing,
        !!editing && styles.whileEditing,
        !!required && !value && styles.emptyButRequired
    )

    //UI Events
    const ref = useRef<HTMLInputElement>(null);
    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key == 'Enter') {
            setEditing(false);
        }
    }
    const onClickEdit = (e: any) => {
        e.stopPropagation();

        setEditing(!readOnly);
        !!ref.current && ref.current.focus();
    }
    const onInput = () => {
        setUserEdited(true);
    }

    //Effects
    useEffect(() => {
        if (!userEdited && !!value) setEditing(false);
    }, [value])

    return (
        <>
            <Input ref={ref}
                className={inputClasses}
                name={name}
                readOnly={readOnly || !editing}
                tabIndex={tabIndex}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                appearance={(readOnly || !editing) ? 'underline' : undefined}
                onInput={onInput}
                onKeyUp={onKeyUp}
                onClick={onClickEdit}
                onBlur={() => !!value && setEditing(false)}
                contentAfter={
                    <>
                        {!editing && !readOnly && (
                            <Button
                                className='hidden'
                                icon={<EditRegular />}
                                appearance='transparent'
                                onClick={onClickEdit}
                                data-testid={`editable-label-edit-button-${name}`}
                            />
                        )}
                    </>
                }
                data-testid={`editable-label-${name}`}
            />
        </>
    );
}